import { CreateParentForm } from '@schooly/components/applications';
import { useNotifications } from '@schooly/components/notifications';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { Loading } from '@schooly/style';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ModalLarge } from '../../../components/uikit-components/Modal/Modal.styled';
import { ApplicationRouteState } from '../../../context/applications/ApplicationsContext';
import { useApplication } from '../../../context/applications/useApplication';
import { getRouteModalPathname } from '../../../helpers/misc';
import { useApplicationForm } from '../useApplicationForm';
import { AddParentModal } from './AddParentModal/AddParentModal';
import { ApplicationCreateModalContent } from './ApplicationCreateModalContent';

export const ApplicationUpdateModal: React.FC = () => {
  const {
    application,
    fetching,
    updateApplication,
    applicationId,
    setPendingParents,
    addParentModal,
  } = useApplication();

  const {
    children,
    parents,
    generalCustomFields,
    addParents,
    removeParent,
    removeChild,
    resetForm,
    prepareFormData,
  } = useApplicationForm('update');

  const { applicationAdultCustomFields, fetching: loadingAdultCustomFields } = useCustomFields();

  const navigate = useNavigate();

  const { showNotification } = useNotifications();
  const { $t } = useIntl();

  const onChildClick = useCallback(
    (id?: string) => {
      if (id) {
        navigate(`child/${id}`);
      } else {
        navigate('child');
      }
    },
    [navigate],
  );

  const onParentEdit = useCallback(
    (id: string) => {
      navigate(`parent/${id}`);
    },
    [navigate],
  );

  const onViewProfile = useCallback(
    (id: string) => {
      const state: ApplicationRouteState = {
        type: 'application',
        actionType: 'update',
        parents,
      };
      navigate(getRouteModalPathname('adult', { id }), { state });
    },
    [navigate, parents],
  );

  const addedParentIds = useMemo(() => {
    return parents.reduce<string[]>((acc, p) => (p.profile_id ? [...acc, p.profile_id] : acc), []);
  }, [parents]);

  const setExistedParents = useCallback(
    (selectedParents: CreateParentForm[]) => {
      const removedParents = addedParentIds.reduce<string[]>((acc, profileId) => {
        const toRemoveId = !selectedParents.some((parent) => profileId === parent.profile_id);
        if (!toRemoveId) return acc;
        const parentId = parents.find((p) => p.profile_id === profileId)?.id;
        return parentId ? [...acc, parentId] : acc;
      }, []);

      if (!!removedParents.length) {
        removedParents.forEach(removeParent);
      }

      addParents(selectedParents);
    },
    [addParents, addedParentIds, parents, removeParent],
  );

  const onContinue = useCallback(() => {
    navigate('general');
  }, [navigate]);

  const onSubmit = useCallback(async () => {
    await updateApplication({ children, parents, generalCustomFields });
    navigate('/applications');

    if (applicationId) {
      navigate('/applications');
      showNotification({
        message: $t({ id: 'applications-SuccessfulUpdate' }),
        type: 'success',
        actions: [
          {
            textId: 'applications-Action-View',
            handler: () =>
              navigate(`/applications/${applicationId}`, {
                replace: true,
              }),
            buttonColor: 'light',
          },
        ],
      });
    }
    resetForm();
  }, [
    $t,
    applicationId,
    children,
    navigate,
    parents,
    resetForm,
    showNotification,
    updateApplication,
    generalCustomFields,
  ]);

  useEffect(() => {
    if (!application || fetching) {
      return;
    }

    if (!children.length && !parents.length) {
      prepareFormData(application);
    }
  }, [application, children, fetching, parents, prepareFormData]);

  const onOpenPreview = useCallback(() => {
    navigate('preview');
  }, [navigate]);

  const onOpenAddParentModal = useCallback(() => {
    setPendingParents(parents);
    addParentModal.onOpen();
  }, [addParentModal, parents, setPendingParents]);

  const onCloseAddParentModal = useCallback(() => {
    setPendingParents([]);
    addParentModal.onClose();
  }, [addParentModal, setPendingParents]);

  return (
    <>
      <ModalLarge open>
        {fetching || !children || !parents || loadingAdultCustomFields ? (
          <Loading />
        ) : (
          <ApplicationCreateModalContent
            children={children}
            parents={parents}
            customFields={{ adult: applicationAdultCustomFields, general: generalCustomFields }}
            onChildClick={onChildClick}
            onChildRemove={removeChild}
            onSubmit={onSubmit}
            onOpenPreview={onOpenPreview}
            onContinue={onContinue}
            resetApplication={resetForm}
            onParentRemove={removeParent}
            parentActions={[
              { titleTextId: 'applications-AddExisting', handler: onOpenAddParentModal },
              { titleTextId: 'applications-CreateNew', handler: () => navigate('parent') },
            ]}
            onParentEdit={onParentEdit}
            onViewProfile={onViewProfile}
            updateParents={addParents}
          />
        )}

        {addParentModal.isOpen && (
          <AddParentModal
            onClose={onCloseAddParentModal}
            addedApplicationParents={parents}
            setApplicationParents={setExistedParents}
          />
        )}
      </ModalLarge>
    </>
  );
};
